import { Component } from '@theme/component';
import { ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} ProductGroupingRefs
 * @property {HTMLElement} selectedLabel - The element showing currently selected/hovered item name
 * @property {HTMLAnchorElement[]} items - The group item swatches/buttons
 * @property {HTMLElement} content - The scroll container of the group items
 * @property {HTMLButtonElement} viewAllBtn - The view all button in grid layout
 * @property {HTMLElement} sliderControls - The slider controls wrapper
 * @property {HTMLButtonElement} prevBtn - Prev button for slider
 * @property {HTMLButtonElement} nextBtn - Next button for slider
 * @property {HTMLElement} pagination - Pagination indicator container
 */

/**
 * A custom web component that manages the product grouping cross-linking PDP display.
 * @extends Component<ProductGroupingRefs>
 */
class ProductGrouping extends Component {
  constructor() {
    super();
    this.originalLabelText = '';
    this.isExpanded = false;
    this.scrollTargets = [];
    this.resizeObserver = null;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Save original selected label
    if (this.refs.selectedLabel) {
      this.originalLabelText = this.refs.selectedLabel.textContent.trim();
    }

    // Setup Hover Previews
    this.setupHoverPreview();

    // Setup View All Toggle
    this.setupViewAllToggle();

    // Setup Slider
    this.setupSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupHoverPreview() {
    const items = this.refs.items || [];
    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (this.refs.selectedLabel && item.dataset.title) {
          this.refs.selectedLabel.textContent = item.dataset.title;
        }
      });

      item.addEventListener('mouseleave', () => {
        if (this.refs.selectedLabel) {
          this.refs.selectedLabel.textContent = this.originalLabelText;
        }
      });
    });
  }

  setupViewAllToggle() {
    if (!this.refs.viewAllBtn) return;

    this.refs.viewAllBtn.addEventListener('click', () => {
      this.isExpanded = !this.isExpanded;
      this.classList.toggle('is-expanded', this.isExpanded);

      // Update button text
      const viewAllText = this.refs.viewAllBtn.dataset.viewAllText || '+ View All';
      const viewLessText = this.refs.viewAllBtn.dataset.viewLessText || '- View Less';
      this.refs.viewAllBtn.textContent = this.isExpanded ? viewLessText : viewAllText;
    });
  }

  get prevBtn() {
    const ref = this.refs.prevBtn;
    if (Array.isArray(ref)) return ref[0];
    return ref || this.querySelector('.variant-slider-arrow--prev');
  }

  get nextBtn() {
    const ref = this.refs.nextBtn;
    if (Array.isArray(ref)) return ref[0];
    return ref || this.querySelector('.variant-slider-arrow--next');
  }

  setupSlider() {
    const content = this.refs.content;
    if (!content) return;

    // Listen to scrolling to toggle arrows and update pagination highlight
    content.addEventListener('scroll', () => this.handleScroll());

    const prev = this.prevBtn;
    if (prev) {
      prev.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollStep(-1);
      });
    }

    const next = this.nextBtn;
    if (next) {
      next.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollStep(1);
      });
    }

    // Setup Resize Observer to recalculate metrics
    this.resizeObserver = new ResizeNotifier(() => this.recalcSlider());
    this.resizeObserver.observe(content);

    // Immediate initial layout call to eliminate any FOUC/column-jump on page refresh
    this.applyMultiRowLayout();
    this.recalcSlider();

    // Secondary run for after fonts/images load
    setTimeout(() => {
      this.recalcSlider();
      this.scrollToActiveItem(false);
    }, 100);
  }

  scrollToActiveItem(smooth = false) {
    const activeItem = this.querySelector('.product-group-item.is-active');
    const content = this.refs.content;
    if (!activeItem || !content) return;
    const itemLeft = activeItem.offsetLeft - content.offsetLeft;
    const itemWidth = activeItem.offsetWidth;
    const containerWidth = content.clientWidth;
    const targetScroll = Math.max(0, itemLeft - (containerWidth / 2) + (itemWidth / 2));
    content.scrollTo({ left: targetScroll, behavior: smooth ? 'smooth' : 'auto' });
  }

  applyMultiRowLayout() {
    const content = this.refs.content;
    if (!content) return;
    const fieldset = this.querySelector('fieldset');
    if (!fieldset) return;

    const isDesktop = window.innerWidth >= 750;
    let rows = 1;
    if (isDesktop) {
      if (fieldset.classList.contains('desktop-slider-rows-3')) rows = 3;
      else if (fieldset.classList.contains('desktop-slider-rows-2')) rows = 2;
    }

    const items = Array.from(this.refs.items || []);
    if (items.length === 0) return;

    if (rows <= 1) {
      items.forEach(item => {
        item.style.gridRow = '';
        item.style.gridColumn = '';
      });
      return;
    }

    const containerWidth = content.clientWidth || content.parentElement?.clientWidth || (window.innerWidth - 32);
    if (containerWidth <= 0) return;

    const firstItem = items[0];
    const itemWidth = firstItem.offsetWidth || 70;
    const columnGap = 8;
    const itemsPerRow = Math.max(1, Math.floor((containerWidth + columnGap) / (itemWidth + columnGap)));
    const pageSize = rows * itemsPerRow;

    items.forEach((item, index) => {
      const page = Math.floor(index / pageSize);
      const indexInPage = index % pageSize;
      const itemRow = Math.floor(indexInPage / itemsPerRow) + 1;
      const colInPage = (indexInPage % itemsPerRow) + 1;
      const itemCol = (page * itemsPerRow) + colInPage;

      item.style.gridRow = `${itemRow}`;
      item.style.gridColumn = `${itemCol}`;
    });
  }

  recalcSlider() {
    const content = this.refs.content;
    if (!content) return;

    this.applyMultiRowLayout();

    const containerWidth = content.clientWidth;
    const scrollWidth = content.scrollWidth;
    const maxScroll = scrollWidth - containerWidth;

    if (maxScroll <= 5) {
      // Not scrollable, hide slider controls
      if (this.refs.sliderControls) {
        this.refs.sliderControls.style.display = 'none';
      }
      this.classList.remove('has-scroll-left', 'has-scroll-right');
      const wrapper = this.querySelector('.variant-display-wrapper');
      if (wrapper) wrapper.classList.remove('has-scroll-left', 'has-scroll-right');
      this.scrollTargets = [];
      return;
    }

    // Calculate page targets
    this.scrollTargets = [0];
    const items = Array.from(this.refs.items || []);
    const pageStep = Math.max(containerWidth - 60, 120);

    let targetPos = pageStep;
    while (targetPos < maxScroll) {
      let bestOffset = targetPos;
      let minDiff = Infinity;
      items.forEach((item) => {
        const itemOffset = item.offsetLeft - content.offsetLeft;
        const diff = Math.abs(itemOffset - targetPos);
        if (diff < minDiff) {
          minDiff = diff;
          bestOffset = itemOffset;
        }
      });

      if (bestOffset > this.scrollTargets[this.scrollTargets.length - 1] + 40 && bestOffset <= maxScroll - 20) {
        this.scrollTargets.push(bestOffset);
      }
      targetPos += pageStep;
    }

    if (maxScroll > this.scrollTargets[this.scrollTargets.length - 1] + 40) {
      this.scrollTargets.push(maxScroll);
    }

    // Show controls
    if (this.refs.sliderControls) {
      this.refs.sliderControls.style.display = 'flex';
    }

    // Rebuild pagination indicators
    this.buildPagination();
    this.handleScroll();
  }

  buildPagination() {
    const pagination = this.refs.pagination;
    if (!pagination) return;

    pagination.innerHTML = '';
    this.scrollTargets.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pagination-number';
      dot.textContent = (index + 1).toString();
      dot.ariaLabel = `Go to page ${index + 1}`;
      dot.addEventListener('click', () => {
        this.scrollToTarget(index);
      });
      pagination.appendChild(dot);
    });
  }

  scrollToTarget(index) {
    const content = this.refs.content;
    const targetLeft = this.scrollTargets[index];
    if (content && targetLeft !== undefined) {
      content.scrollTo({
        left: targetLeft,
        behavior: 'smooth'
      });
    }
  }

  scrollStep(direction) {
    const content = this.refs.content;
    if (!content) return;

    const currentScroll = content.scrollLeft;
    // Find index of the closest target
    let activeIndex = 0;
    let minDiff = Infinity;
    this.scrollTargets.forEach((target, index) => {
      const diff = Math.abs(target - currentScroll);
      if (diff < minDiff) {
        minDiff = diff;
        activeIndex = index;
      }
    });

    const targetIndex = activeIndex + direction;
    if (targetIndex >= 0 && targetIndex < this.scrollTargets.length) {
      this.scrollToTarget(targetIndex);
    }
  }

  handleScroll() {
    const content = this.refs.content;
    if (!content) return;

    const currentScroll = content.scrollLeft;
    const containerWidth = content.clientWidth;
    const scrollWidth = content.scrollWidth;
    const maxScroll = scrollWidth - containerWidth;

    // Highlight active pagination indicator
    let activeIndex = 0;
    let minDiff = Infinity;
    this.scrollTargets.forEach((target, index) => {
      const diff = Math.abs(target - currentScroll);
      if (diff < minDiff) {
        minDiff = diff;
        activeIndex = index;
      }
    });

    const dots = this.querySelectorAll('.pagination-number');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });

    // Toggle arrow disabled states based on activeIndex & scroll position
    const isAtStart = (activeIndex === 0 || currentScroll <= 15);
    const isAtEnd = (this.scrollTargets.length > 0 && activeIndex === this.scrollTargets.length - 1) || Math.ceil(currentScroll) >= Math.floor(maxScroll) - 15;

    const atScrollStart = currentScroll <= 5;
    const atScrollEnd = currentScroll >= maxScroll - 5;
    this.classList.toggle('has-scroll-left', !atScrollStart);
    this.classList.toggle('has-scroll-right', !atScrollEnd);
    const wrapper = this.querySelector('.variant-display-wrapper');
    if (wrapper) {
      wrapper.classList.toggle('has-scroll-left', !atScrollStart);
      wrapper.classList.toggle('has-scroll-right', !atScrollEnd);
    }

    const prev = this.prevBtn;
    if (prev) {
      if (isAtStart) {
        prev.setAttribute('disabled', 'disabled');
        prev.disabled = true;
      } else {
        prev.removeAttribute('disabled');
        prev.disabled = false;
      }
    }

    const next = this.nextBtn;
    if (next) {
      if (isAtEnd) {
        next.setAttribute('disabled', 'disabled');
        next.disabled = true;
      } else {
        next.removeAttribute('disabled');
        next.disabled = false;
      }
    }
  }
}

if (!customElements.get('product-grouping')) {
  customElements.define('product-grouping', ProductGrouping);
}
export { ProductGrouping };
